// Plays the celebration queue: one dialog per earned milestone, oldest first.
// Mounting it is what releases the queue, so only pause moments (the library)
// render it. It starts the next dialog only while the tab is visible and no
// other dialog is open. Any way of closing it acknowledges the milestone.
//
// The milestone flips in as a floating collectible. Collect spins it away
// like a tossed coin and the reward lands where it was, while the button
// shows a spinner; then the dialog closes.

import React, { useEffect, useState } from 'react';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type MotionProps,
} from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAnyDialogOpen } from '@/components/ui/dialogPresenceStore';
import { Spinner } from '@/components/ui/spinner';
import type { PendingCompletion } from '../../../contexts';
import { useAchievements, useDocumentVisible } from '../../../hooks';
import { RewardChip } from './AchievementChips';
import AchievementCollectible from './AchievementCollectible';
import AchievementRewardReveal from './AchievementRewardReveal';

// After Collect: the card spins away, and the reward starts in just before
// it is gone.
const CARD_EXIT_S = 0.6;
const REWARD_DELAY_S = CARD_EXIT_S - 0.1;
// How long the reward stays on screen after Collect, including its entrance.
const REWARD_HOLD_MS = 2300;

const PRESERVE_3D = { transformStyle: 'preserve-3d' } as const;

const completionId = ({ track, milestone }: PendingCompletion): string =>
  `${track.achievementKey}:${milestone.milestoneKey}`;

/** The card's entrance and exit; plain fades with reduced motion. */
const cardMotion = (reduceMotion: boolean): MotionProps =>
  reduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.2 },
        exit: { opacity: 0, transition: { duration: 0.2 } },
      }
    : {
        // It starts under 90°, where the back face and the content depth
        // would swing into view, and settles without overshoot, which read
        // as a second pop.
        initial: { opacity: 0, rotateY: -70, scale: 0.85 },
        animate: { opacity: 1, rotateY: 0, scale: 1 },
        transition: { type: 'spring', stiffness: 120, damping: 20 },
        // A tossed coin: it spins up and away.
        exit: {
          opacity: [1, 1, 0],
          rotateY: 720,
          scale: 0.15,
          y: -40,
          transition: { duration: CARD_EXIT_S, ease: [0.55, 0, 0.8, 0.4] },
        },
      };

const AchievementDialog: React.FC = () => {
  const { pending, acknowledge } = useAchievements();
  const visible = useDocumentVisible();
  const reduceMotion = useReducedMotion() ?? false;
  // Includes this dialog until its close animation ends, so the next one waits.
  const anyDialogOpen = useAnyDialogOpen();
  // Kept after closing so the content can animate out.
  const [current, setCurrent] = useState<PendingCompletion | null>(null);
  const [open, setOpen] = useState(false);
  const [collected, setCollected] = useState(false);

  const next = pending[0] ?? null;
  useEffect(() => {
    if (open || !next || !visible || anyDialogOpen) return;
    setCurrent(next);
    setCollected(false);
    setOpen(true);
  }, [open, next, visible, anyDialogOpen]);

  // Another tab acknowledged it before this one collected it.
  const currentPending =
    current !== null &&
    pending.some((item) => completionId(item) === completionId(current));
  useEffect(() => {
    if (open && !collected && !currentPending) setOpen(false);
  }, [open, collected, currentPending]);

  // After Collect, show the reward for a moment, then close.
  useEffect(() => {
    if (!open || !collected) return;
    const timer = setTimeout(() => setOpen(false), REWARD_HOLD_MS);
    return () => clearTimeout(timer);
  }, [open, collected]);

  const collect = () => {
    if (!current || collected) return;
    acknowledge(current);
    setCollected(true);
  };

  if (!current) return null;
  const { track, milestone, level } = current;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) return;
        // Esc or a click outside skips the reward animation.
        if (!collected) acknowledge(current);
        setOpen(false);
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="justify-items-center gap-5 p-6 text-center sm:max-w-sm"
      >
        <DialogTitle className="text-2xl font-semibold">
          Achievement unlocked!
        </DialogTitle>
        {/* The card and the reward share one slot, so the dialog keeps its
            size. The slot's perspective gives both their depth. */}
        <div className="grid min-h-64 w-full place-items-center [perspective:900px]">
          {/* No initial={false} here: it would also stop the card's float,
              which starts on mount. */}
          <AnimatePresence>
            {collected ? (
              <motion.div
                key="reward"
                className="[grid-area:1/1]"
                style={PRESERVE_3D}
              >
                <AchievementRewardReveal
                  reward={milestone.reward}
                  delay={reduceMotion ? 0 : REWARD_DELAY_S}
                />
              </motion.div>
            ) : (
              <motion.div
                key="card"
                className="w-full [grid-area:1/1]"
                style={PRESERVE_3D}
                {...cardMotion(reduceMotion)}
              >
                <AchievementCollectible
                  mode="float"
                  track={track}
                  level={level}
                  description={
                    <DialogDescription className="text-current">
                      {milestone.goal}
                    </DialogDescription>
                  }
                  chip={
                    <RewardChip
                      reward={milestone.reward}
                      accent={track.accent}
                      level={level}
                    />
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Button
          size="xl"
          className="w-full"
          disabled={collected}
          onClick={collect}
        >
          {/* Busy while the card and the reward play out, then the dialog
              closes. */}
          {collected ? (
            <Spinner className="size-5 text-primary-foreground" />
          ) : (
            'Collect'
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default AchievementDialog;
